import { useState, useEffect, useCallback, useRef } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { SortableTableHeader } from '../components/ui/sortable-table-header';
import { Plus, MagnifyingGlass, PencilSimple, Trash, Cube, ShieldCheck, Upload, X, Image, SpinnerGap, CaretLeft, CaretRight, Database, CloudArrowUp, User } from '@phosphor-icons/react';
import { toast } from 'sonner';
import EmptyState from '../components/EmptyState';
import { useNavigate } from 'react-router-dom';

const PAGE_SIZE = 20;

export default function ApplicationsPage() {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', owner: '', owner_email: '', roles: [], status: 'active', logo_path: null, connector_type: 'azure_ad' });
  const [editing, setEditing] = useState(null);
  const [roles, setRoles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [connectorFilter, setConnectorFilter] = useState('');
  const [appStatusFilter, setAppStatusFilter] = useState('');

  const load = useCallback(async () => {
    try {
      const skip = (currentPage - 1) * PAGE_SIZE;
      const res = await API.get('/applications', { 
        params: { 
          search: search || undefined, 
          limit: PAGE_SIZE,
          skip: skip,
          sort_by: sortBy,
          sort_order: sortOrder,
          connector_type: connectorFilter || undefined,
          status: appStatusFilter || undefined
        } 
      });
      setApps(res.data.items);
      setTotal(res.data.total);
    } catch (err) { toast.error('Failed to load applications'); }
  }, [search, currentPage, sortBy, sortOrder, connectorFilter, appStatusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, sortBy, sortOrder, connectorFilter, appStatusFilter]);

  useEffect(() => {
    API.get('/roles', { params: { limit: 200 } }).then(r => {
      setRoles(r.data.items);
    }).catch(() => {});
  }, []);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleSort = (column, order) => {
    setSortBy(column);
    setSortOrder(order);
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload a JPG, PNG, GIF, WebP, or SVG image.');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await API.post('/applications/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({ ...prev, logo_path: res.data.path }));
      // Create preview URL
      setLogoPreview(URL.createObjectURL(file));
      toast.success('Logo uploaded successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = () => {
    setForm(prev => ({ ...prev, logo_path: null }));
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getLogoUrl = (logoPath) => {
    if (!logoPath) return null;
    return `${API.defaults.baseURL}/applications/logo/${logoPath}`;
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await API.put(`/applications/${editing}`, form);
        toast.success('Application updated');
      } else {
        await API.post('/applications', form);
        toast.success('Application created');
      }
      setShowDialog(false);
      setForm({ name: '', description: '', owner: '', owner_email: '', roles: [], status: 'active', logo_path: null, connector_type: 'azure_ad' });
      setLogoPreview(null);
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleEdit = (app) => {
    setForm({ 
      name: app.name, 
      description: app.description || '', 
      owner: app.owner || '', 
      owner_email: app.owner_email || '',
      roles: app.roles || [], 
      status: app.status || 'active',
      logo_path: app.logo_path || null,
      connector_type: app.connector_type || 'azure_ad'
    });
    setLogoPreview(app.logo_path ? getLogoUrl(app.logo_path) : null);
    setEditing(app.id);
    setShowDialog(true);
  };

  const openCreateDialog = () => {
    setForm({ name: '', description: '', owner: '', owner_email: '', roles: [], status: 'active', logo_path: null, connector_type: 'azure_ad' });
    setLogoPreview(null);
    setEditing(null);
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this application?')) return;
    try { await API.delete(`/applications/${id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error('Failed to delete'); }
  };

  const statusColor = (s) => {
    if (s === 'active') return 'bg-emerald-100 text-emerald-700';
    if (s === 'inactive') return 'bg-slate-100 text-navy-600';
    return 'bg-amber-100 text-amber-700';
  };

  return (
    <div data-testid="applications-page" className="p-4 sm:p-6 md:p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-navy-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Applications</h1>
          <p className="text-sm text-navy-500 mt-1">{total} applications registered • Request access to applications to get assigned roles</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm shadow-sm" data-testid="create-application-button">
          <Plus size={16} className="mr-1.5" /> New Application
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
          <Input placeholder="Search applications..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9 rounded-sm border-slate-200" />
        </div>
        <Select value={connectorFilter || 'all'} onValueChange={(v) => setConnectorFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-36 h-9 rounded-sm text-xs" data-testid="app-connector-filter">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="azure_ad">Azure AD</SelectItem>
            <SelectItem value="manual">Manual / CSV</SelectItem>
          </SelectContent>
        </Select>
        <Select value={appStatusFilter || 'all'} onValueChange={(v) => setAppStatusFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-28 h-9 rounded-sm text-xs" data-testid="app-status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        {(connectorFilter || appStatusFilter) && (
          <Button variant="ghost" size="sm" onClick={() => { setConnectorFilter(''); setAppStatusFilter(''); }} className="text-xs text-navy-500 h-9">Clear filters</Button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        <div className="h-1.5 bg-navy-600" />
        <Table wrapperClassName="max-h-[calc(100vh-340px)]">
          <TableHeader>
            <TableRow className="bg-slate-50 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
              <SortableTableHeader column="name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Application</SortableTableHeader>
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Connector</TableHead>
              <SortableTableHeader column="owner" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Owner</SortableTableHeader>
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500">Roles/Entitlements</TableHead>
              <SortableTableHeader column="status" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Status</SortableTableHeader>
              <TableHead className="text-[10px] font-bold uppercase tracking-wider text-navy-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {apps.length === 0 ? (
              <TableRow><TableCell colSpan={6}><EmptyState type="applications" onAction={() => setShowDialog(true)} /></TableCell></TableRow>
            ) : apps.map((app) => (
              <TableRow key={app.id} className="hover:bg-slate-50 transition-colors" data-testid={`app-row-${app.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {app.logo_path ? (
                      <div className="w-10 h-10 rounded-sm overflow-hidden bg-slate-100 flex items-center justify-center">
                        <img 
                          src={getLogoUrl(app.logo_path)} 
                          alt={app.name} 
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />
                        <div className="hidden w-full h-full items-center justify-center">
                          <Cube size={20} className="text-orange-600" />
                        </div>
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-sm bg-orange-100 flex items-center justify-center">
                        <Cube size={20} className="text-orange-600" />
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-sm text-navy-900">{app.name}</p>
                      <p className="text-xs text-navy-400 truncate max-w-xs">{app.description || 'No description'}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {app.connector_type === 'manual' ? (
                    <Badge className="bg-purple-100 text-purple-700 text-[10px]">
                      <Database size={10} className="mr-1" /> Manual/CSV
                    </Badge>
                  ) : (
                    <Badge className="bg-blue-100 text-blue-700 text-[10px]">
                      <CloudArrowUp size={10} className="mr-1" /> Azure AD
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <p className="text-navy-900">{app.owner || '-'}</p>
                    {app.owner_email && <p className="text-xs text-navy-400">{app.owner_email}</p>}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {app.connector_type === 'manual' ? (
                      <Badge 
                        className="bg-purple-100 text-purple-700 text-[10px] cursor-pointer hover:bg-purple-200"
                        onClick={() => navigate(`/app-accounts/${app.id}`)}
                      >
                        <User size={10} className="mr-1" /> Manage Accounts
                      </Badge>
                    ) : (app.role_details || []).length > 0 ? (
                      <>
                        {app.role_details.slice(0, 3).map(r => (
                          <Badge key={r.id} className="bg-blue-100 text-blue-700 text-[10px]">
                            <ShieldCheck size={10} className="mr-1" />{r.name}
                          </Badge>
                        ))}
                        {app.role_details.length > 3 && (
                          <Badge className="bg-slate-100 text-navy-600 text-[10px]">+{app.role_details.length - 3} more</Badge>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-navy-400">No roles assigned</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={`text-[10px] ${statusColor(app.status)}`}>{app.status || 'active'}</Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {app.connector_type === 'manual' && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => navigate(`/app-accounts/${app.id}`)}
                        title="Manage Accounts"
                        data-testid={`manage-accounts-${app.id}`}
                      >
                        <User size={14} className="text-purple-600" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(app)} data-testid={`edit-app-${app.id}`}><PencilSimple size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(app.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-app-${app.id}`}><Trash size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {total > 0 && (
        <div className="flex items-center justify-between bg-white border border-slate-200 rounded-sm shadow-sm px-4 py-3">
          <div className="text-sm text-navy-500">
            Showing <span className="font-medium text-navy-700">{((currentPage - 1) * PAGE_SIZE) + 1}</span> to{' '}
            <span className="font-medium text-navy-700">{Math.min(currentPage * PAGE_SIZE, total)}</span> of{' '}
            <span className="font-medium text-navy-700">{total}</span> applications
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="rounded-sm h-8 px-3"
            >
              <CaretLeft size={14} className="mr-1" /> Previous
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className={`rounded-sm h-8 w-8 p-0 ${currentPage === pageNum ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}`}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="rounded-sm h-8 px-3"
            >
              Next <CaretRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-sm bg-blue-100 flex items-center justify-center shrink-0">
            <Cube size={16} className="text-blue-600" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-blue-900" style={{ fontFamily: 'Chivo, sans-serif' }}>How Applications Work</h4>
            <p className="text-xs text-blue-700 mt-1">
              Applications group <strong>roles</strong> together for easy access requesting. When a user requests access to an application, 
              they receive all the roles assigned to that application. Each role contains specific entitlements (permissions).
            </p>
            <p className="text-xs text-blue-600 mt-2">
              <strong>Access Flow:</strong> User → Requests Application → Gets Roles → Roles grant Entitlements
            </p>
          </div>
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>{editing ? 'Edit Application' : 'Create Application'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {/* Logo Upload Section */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Application Logo</Label>
              <div className="mt-2 flex items-start gap-4">
                <div className="w-20 h-20 rounded-sm border-2 border-dashed border-slate-200 flex items-center justify-center overflow-hidden bg-slate-50">
                  {logoPreview || form.logo_path ? (
                    <img 
                      src={logoPreview || getLogoUrl(form.logo_path)} 
                      alt="Logo preview" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Image size={24} className="text-navy-300" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleLogoUpload}
                    accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                    className="hidden"
                    data-testid="logo-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="rounded-sm text-xs"
                    data-testid="upload-logo-button"
                  >
                    {uploading ? (
                      <><SpinnerGap size={14} className="mr-1.5 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload size={14} className="mr-1.5" /> Upload Logo</>
                    )}
                  </Button>
                  {(logoPreview || form.logo_path) && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={removeLogo}
                      className="rounded-sm text-xs text-red-500 hover:text-red-700"
                      data-testid="remove-logo-button"
                    >
                      <X size={14} className="mr-1" /> Remove
                    </Button>
                  )}
                  <p className="text-[10px] text-navy-400">JPG, PNG, GIF, WebP, or SVG. Max 5MB.</p>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="mt-1 rounded-sm" data-testid="app-name-input" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Description</Label>
              <Input value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="mt-1 rounded-sm" placeholder="Brief description of what this application provides access to" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Owner Name</Label>
                <Input value={form.owner} onChange={(e) => setForm({...form, owner: e.target.value})} className="mt-1 rounded-sm" placeholder="Application owner" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Owner Email</Label>
                <Input value={form.owner_email} onChange={(e) => setForm({...form, owner_email: e.target.value})} className="mt-1 rounded-sm" placeholder="owner@company.com" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger className="mt-1 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="deprecated">Deprecated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Connector Type</Label>
              <Select value={form.connector_type} onValueChange={(v) => setForm({...form, connector_type: v})} disabled={!!editing}>
                <SelectTrigger className="mt-1 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="azure_ad">
                    <div className="flex items-center gap-2">
                      <CloudArrowUp size={14} className="text-blue-600" />
                      <span>Azure AD (Uses IAG Roles)</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="manual">
                    <div className="flex items-center gap-2">
                      <Database size={14} className="text-purple-600" />
                      <span>Manual/CSV (Own Accounts & Entitlements)</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-navy-400 mt-1">
                {form.connector_type === 'manual' 
                  ? 'Import accounts and entitlements via CSV. Changes create provisioning tickets.' 
                  : 'Uses Azure AD groups as entitlements, bundled into IAG roles.'}
              </p>
            </div>
            {form.connector_type !== 'manual' && (
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider mb-2 block">Assign Roles</Label>
              <p className="text-xs text-navy-500 mb-2">Users who request this application will receive all selected roles</p>
              <div className="border border-slate-200 rounded-sm max-h-48 overflow-y-auto">
                {roles.length === 0 ? <p className="p-3 text-sm text-navy-400">No roles available. Create roles first.</p> :
                  roles.map(r => (
                    <label key={r.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0">
                      <Checkbox 
                        checked={form.roles.includes(r.id)} 
                        onCheckedChange={() => setForm(prev => ({ 
                          ...prev, 
                          roles: prev.roles.includes(r.id) 
                            ? prev.roles.filter(x => x !== r.id) 
                            : [...prev.roles, r.id] 
                        }))} 
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <ShieldCheck size={14} className="text-blue-600" />
                          <span className="text-sm font-medium">{r.name}</span>
                        </div>
                        {r.description && <p className="text-xs text-navy-400 mt-0.5">{r.description}</p>}
                      </div>
                      {r.entitlements?.length > 0 && (
                        <Badge className="bg-slate-100 text-navy-600 text-[10px]">{r.entitlements.length} entitlements</Badge>
                      )}
                    </label>
                  ))
                }
              </div>
            </div>
            )}
            {form.connector_type === 'manual' && !editing && (
              <div className="bg-purple-50 border border-purple-200 rounded-sm p-3">
                <p className="text-xs text-purple-700">
                  <strong>Note:</strong> After creating this application, you'll be able to import accounts and entitlements via CSV.
                  Go to the application's management page to upload data.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="save-application-button">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
